export class A11yDropdown {
  constructor(element) {
    this.base = element;
    this.trigger = element.querySelector('[data-dropdown-trigger]');
    this.icon = this.trigger ? this.trigger.querySelector('[data-dropdown-icon]') : null;
    this.panel = element.querySelector('[data-dropdown-panel]');
    this.group = element.dataset.dropdownGroup || 'default';
    this.layer = element.dataset.dropdownLayer || 'inline';
    this.usePortalLayer = this.layer === 'portal';
    this.portalParent = this.panel ? this.panel.parentElement : null;
    this.portalNextSibling = this.panel ? this.panel.nextSibling : null;
    this.instanceId = A11yDropdown.nextId = (A11yDropdown.nextId || 0) + 1;

    if (!this.trigger || !this.panel) {
      return;
    }

    this.base.A11yDropdown = this;
    A11yDropdown.instances = A11yDropdown.instances || [];
    A11yDropdown.instances.push(this);

    this._configureAriaIds();
    this._close();
    this._bindEvents();
  }

  _bindEvents() {
    this.trigger.addEventListener('click', () => this._toggle());
    this.trigger.addEventListener('keydown', (e) => this._onTriggerKeyDown(e));

    this.panel.addEventListener('keydown', (e) => {
      this._setInteractionMode('keyboard');
      this._onPanelKeyDown(e);
    });
    this.panel.addEventListener('focusin', (e) => this._onPanelFocusIn(e));
    this.panel.addEventListener('focusout', (e) => this._onPanelFocusOut(e));

    this.panel.addEventListener('click', (e) => {
      if (e.target.closest('a, button')) {
        this._close();
      }
    });

    this._onViewportChange = () => {
      if (this.base.classList.contains('is-active') && this.usePortalLayer) {
        this._positionPanel();
      }
    };

    window.addEventListener('resize', this._onViewportChange);
    window.addEventListener('scroll', this._onViewportChange, true);

    document.addEventListener('mousedown', (e) => {
      if (!this.base.contains(e.target) && !this.panel.contains(e.target)) {
        this._close();
      }
    });
  }

  _configureAriaIds() {
    const triggerId = this.trigger.id || `generic-dropdown-trigger-${this.instanceId}`;
    const panelId = this.panel.id || `generic-dropdown-panel-${this.instanceId}`;

    this.trigger.setAttribute('id', triggerId);
    this.trigger.setAttribute('aria-controls', panelId);
    this.trigger.setAttribute('aria-expanded', 'false');

    this.panel.setAttribute('id', panelId);
    this.panel.setAttribute('aria-labelledby', triggerId);
    this.panel.setAttribute('aria-hidden', 'true');
  }

  _getFocusableItems() {
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null);
  }

  _toggle() {
    this.base.classList.contains('is-active') ? this._close() : this._open('none', 'pointer');
  }

  _setInteractionMode(mode) {
    this.base.setAttribute('data-dropdown-input', mode);
  }

  _isFocusableMenuItem(element) {
    return (
      element &&
      element.matches &&
      element.matches('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  _clearFocusVisible() {
    this.panel
      .querySelectorAll('.is-focus-visible')
      .forEach((el) => el.classList.remove('is-focus-visible'));
  }

  _onPanelFocusIn(e) {
    if (this.base.getAttribute('data-dropdown-input') !== 'keyboard') {
      return;
    }

    if (!this._isFocusableMenuItem(e.target)) {
      return;
    }

    this._clearFocusVisible();
    e.target.classList.add('is-focus-visible');
  }

  _onPanelFocusOut(e) {
    if (this._isFocusableMenuItem(e.target)) {
      e.target.classList.remove('is-focus-visible');
    }
  }

  _open(focusTarget = 'none', interactionMode = 'pointer') {
    if (this.base.classList.contains('is-active')) {
      return;
    }

    this._closeOtherInGroup();
    this._setInteractionMode(interactionMode);

    this.base.classList.add('is-active');
    this.base.setAttribute('data-dropdown', 'opened');
    this.trigger.setAttribute('aria-expanded', 'true');
    if (this.icon && this.icon.dataset.dropdownIconOpened) {
      this.icon.src = this.icon.dataset.dropdownIconOpened;
    }
    this.panel.setAttribute('aria-hidden', 'false');
    this.panel.hidden = false;

    if (this.usePortalLayer) {
      this._mountToPortalLayer();
      this._positionPanel();
    }

    if (focusTarget === 'none') {
      this._clearFocusVisible();
      return;
    }

    const items = this._getFocusableItems();
    if (!items.length) {
      return;
    }

    if (focusTarget === 'last') {
      items[items.length - 1].focus();
      return;
    }

    items[0].focus();
  }

  _close(shouldFocusTrigger = false) {
    this.base.classList.remove('is-active');
    this.base.setAttribute('data-dropdown', 'closed');
    this.trigger.setAttribute('aria-expanded', 'false');
    if (this.icon && this.icon.dataset.dropdownIconClosed) {
      this.icon.src = this.icon.dataset.dropdownIconClosed;
    }
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.hidden = true;
    this._clearFocusVisible();

    if (this.usePortalLayer) {
      this._restoreFromPortalLayer();
    }

    if (shouldFocusTrigger) {
      this.trigger.focus();
    }
  }

  _mountToPortalLayer() {
    if (this.panel.parentElement === document.body) {
      return;
    }

    this.panel.classList.add('is-portal-layer');
    document.body.appendChild(this.panel);
  }

  _restoreFromPortalLayer() {
    if (!this.portalParent || this.panel.parentElement !== document.body) {
      return;
    }

    this.panel.classList.remove('is-portal-layer');
    this.panel.style.top = '';
    this.panel.style.left = '';
    this.panel.style.right = '';
    this.panel.style.transform = '';

    if (this.portalNextSibling && this.portalNextSibling.parentNode === this.portalParent) {
      this.portalParent.insertBefore(this.panel, this.portalNextSibling);
      return;
    }

    this.portalParent.appendChild(this.panel);
  }

  _positionPanel() {
    if (!this.usePortalLayer || this.panel.parentElement !== document.body) {
      return;
    }

    const triggerRect = this.trigger.getBoundingClientRect();
    const panelRect = this.panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const panelWidth = Math.round(panelRect.width);

    let left = Math.round(triggerRect.left);
    const overflowRight = left + panelWidth - viewportWidth;

    if (overflowRight > 0) {
      left = Math.round(triggerRect.right - panelWidth);
    }

    left = Math.max(0, Math.min(left, Math.max(0, viewportWidth - panelWidth)));

    this.panel.style.top = `${Math.round(triggerRect.bottom)}px`;
    this.panel.style.left = `${left}px`;
    this.panel.style.right = 'auto';
    this.panel.style.transform = 'none';
  }

  _closeOtherInGroup() {
    (A11yDropdown.instances || []).forEach((instance) => {
      if (instance !== this && instance.group === this.group) {
        instance._close();
      }
    });
  }

  _onTriggerKeyDown(e) {
    this._setInteractionMode('keyboard');

    if (e.key === 'Tab' && this.base.classList.contains('is-active')) {
      const items = this._getFocusableItems();

      if (items.length) {
        e.preventDefault();
        items[e.shiftKey ? items.length - 1 : 0].focus();
      }

      return;
    }

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this._open('first', 'keyboard');
        break;
      case 'ArrowDown':
        e.preventDefault();
        this._open('first', 'keyboard');
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._open('last', 'keyboard');
        break;
      case 'Escape':
        e.preventDefault();
        this._close();
        break;
    }
  }

  _onPanelKeyDown(e) {
    const items = this._getFocusableItems();
    if (!items.length) {
      return;
    }

    const currentIndex = items.indexOf(document.activeElement);
    const nextIndex = (currentIndex + 1) % items.length;
    const prevIndex = (currentIndex - 1 + items.length) % items.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        items[nextIndex].focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[prevIndex].focus();
        break;
      case 'Home':
        e.preventDefault();
        items[0].focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1].focus();
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        if (currentIndex >= 0) {
          items[currentIndex].click();
        }
        break;
      case 'Escape':
        e.preventDefault();
        this._close(true);
        break;
      case 'Tab':
        e.preventDefault();
        items[e.shiftKey ? prevIndex : nextIndex].focus();
        break;
    }
  }
}
